/**
 * File System Access API wrapper for local database files
 *
 * Provides cross-browser file operations:
 * - Chrome/Edge: File System Access API (persistent handle)
 * - Firefox/Safari: Fallback to download/upload pattern
 *
 * Supports optional encryption:
 * - Encrypted files are detected by magic header "QGEN"
 * - Password required to decrypt on open
 * - Password optional when saving (encrypts if provided)
 */

import { Database } from "sql.js";
import {
  openDatabaseFromFile,
  openDatabaseFromBytes,
  exportDatabaseBytes,
} from "./local-db";
import {
  encryptDatabase,
  decryptDatabase,
  isEncryptedDatabase,
} from "./crypto";

// ============================================================================
// Types
// ============================================================================

/**
 * Extended FileSystemFileHandle with permission methods
 */
export interface FileSystemFileHandleExt extends FileSystemFileHandle {
  queryPermission?(options: { mode: "readwrite" }): Promise<PermissionState>;
  requestPermission?(options: { mode: "readwrite" }): Promise<PermissionState>;
}

/**
 * Result of opening a database file
 */
export interface OpenDatabaseResult {
  db: Database;
  handle: FileSystemFileHandleExt | null;
  fileName: string;
  isEncrypted: boolean;
}

/**
 * Result of checking a file for encryption
 */
export interface FileCheckResult {
  isEncrypted: boolean;
  file: File;
  handle: FileSystemFileHandleExt | null;
  fileName: string;
}

/**
 * Check if File System Access API is supported
 */
export function isFileSystemAccessSupported(): boolean {
  return "showOpenFilePicker" in window && "showSaveFilePicker" in window;
}

// ============================================================================
// File System Access API (Chrome/Edge)
// ============================================================================

/**
 * Pick a database file and check if it's encrypted.
 * Does NOT open the database - caller must handle encryption if needed.
 */
export async function pickAndCheckDatabaseFile(): Promise<FileCheckResult> {
  if (!isFileSystemAccessSupported()) {
    return pickAndCheckDatabaseFileFallback();
  }

  try {
    const [handle] = await window.showOpenFilePicker({
      types: [
        {
          description: "QuickGrade Database",
          accept: {
            "application/x-sqlite3": [".db", ".sqlite", ".sqlite3"],
          },
        },
      ],
      multiple: false,
    });

    const file = await handle.getFile();
    const bytes = new Uint8Array(await file.arrayBuffer());
    const encrypted = isEncryptedDatabase(bytes);

    return {
      isEncrypted: encrypted,
      file,
      handle: handle as FileSystemFileHandleExt,
      fileName: file.name,
    };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error("File picker was cancelled");
    }
    throw err;
  }
}

/**
 * Open a database file using File System Access API.
 * Returns both the database and a file handle for future saves.
 *
 * @deprecated Use pickAndCheckDatabaseFile + openDatabaseWithPassword for encrypted support
 */
export async function pickDatabaseFile(): Promise<OpenDatabaseResult> {
  if (!isFileSystemAccessSupported()) {
    return pickDatabaseFileFallback();
  }

  try {
    const [handle] = await window.showOpenFilePicker({
      types: [
        {
          description: "QuickGrade Database",
          accept: {
            "application/x-sqlite3": [".db", ".sqlite", ".sqlite3"],
          },
        },
      ],
      multiple: false,
    });

    const file = await handle.getFile();
    const db = await openDatabaseFromFile(file);

    return {
      db,
      handle: handle as FileSystemFileHandleExt,
      fileName: file.name,
      isEncrypted: false,
    };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error("File picker was cancelled");
    }
    throw err;
  }
}

/**
 * Open a database from a FileCheckResult, optionally with decryption.
 *
 * @param checkResult - Result from pickAndCheckDatabaseFile
 * @param password - Password for decryption (required if encrypted)
 * @returns Opened database result
 */
export async function openDatabaseWithPassword(
  checkResult: FileCheckResult,
  password?: string
): Promise<OpenDatabaseResult> {
  const bytes = new Uint8Array(await checkResult.file.arrayBuffer());

  if (checkResult.isEncrypted) {
    if (!password) {
      throw new Error("Password required for encrypted database");
    }

    // Decrypt the database
    const decryptedBytes = await decryptDatabase(bytes, password);
    const db = await openDatabaseFromBytes(decryptedBytes);

    return {
      db,
      handle: checkResult.handle,
      fileName: checkResult.fileName,
      isEncrypted: true,
    };
  } else {
    // Open directly
    const db = await openDatabaseFromFile(checkResult.file);

    return {
      db,
      handle: checkResult.handle,
      fileName: checkResult.fileName,
      isEncrypted: false,
    };
  }
}

/**
 * Save database to the existing file handle, or prompt for new location.
 * Optionally encrypts with password.
 *
 * @param db - Database to save
 * @param existingHandle - Existing file handle (null prompts for location)
 * @param password - Optional password for encryption
 * @returns File handle (null for fallback mode)
 */
export async function saveDatabaseFile(
  db: Database,
  existingHandle: FileSystemFileHandleExt | null,
  password?: string | null
): Promise<FileSystemFileHandleExt | null> {
  if (!isFileSystemAccessSupported()) {
    saveDatabaseFileFallback(db, "quickgrade.db", password);
    return null;
  }

  let handle = existingHandle;

  // If no existing handle, prompt user for save location
  if (!handle) {
    try {
      handle = (await window.showSaveFilePicker({
        suggestedName: "quickgrade.db",
        types: [
          {
            description: "QuickGrade Database",
            accept: {
              "application/x-sqlite3": [".db"],
            },
          },
        ],
      })) as FileSystemFileHandleExt;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        throw new Error("Save was cancelled");
      }
      throw err;
    }
  }

  // Check/request write permission
  if (handle.queryPermission && handle.requestPermission) {
    const permission = await handle.queryPermission({ mode: "readwrite" });
    if (permission !== "granted") {
      const requested = await handle.requestPermission({ mode: "readwrite" });
      if (requested !== "granted") {
        throw new Error("Write permission denied");
      }
    }
  }

  // Get database bytes
  let dataToWrite: Uint8Array = exportDatabaseBytes(db);

  // Encrypt if password provided
  if (password) {
    dataToWrite = await encryptDatabase(dataToWrite, password);
  }

  // Write the database
  const writable = await handle.createWritable();
  await writable.write(dataToWrite.buffer.slice(0) as ArrayBuffer);
  await writable.close();

  return handle;
}

/**
 * Create a new database and prompt for save location.
 * Optionally encrypts with password.
 *
 * @param db - Database to save
 * @param password - Optional password for encryption
 * @returns Database result with encryption status
 */
export async function createNewDatabaseFile(
  db: Database,
  password?: string | null
): Promise<OpenDatabaseResult> {
  const isEncrypted = !!password;

  if (!isFileSystemAccessSupported()) {
    saveDatabaseFileFallback(db, "quickgrade.db", password);
    return {
      db,
      handle: null,
      fileName: "quickgrade.db",
      isEncrypted,
    };
  }

  try {
    const handle = (await window.showSaveFilePicker({
      suggestedName: "quickgrade.db",
      types: [
        {
          description: "QuickGrade Database",
          accept: {
            "application/x-sqlite3": [".db"],
          },
        },
      ],
    })) as FileSystemFileHandleExt;

    // Get database bytes
    let dataToWrite: Uint8Array = exportDatabaseBytes(db);

    // Encrypt if password provided
    if (password) {
      dataToWrite = await encryptDatabase(dataToWrite, password);
    }

    // Write initial database
    const writable = await handle.createWritable();
    await writable.write(dataToWrite.buffer.slice(0) as ArrayBuffer);
    await writable.close();

    return {
      db,
      handle,
      fileName: handle.name,
      isEncrypted,
    };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error("Save was cancelled");
    }
    throw err;
  }
}

// ============================================================================
// Fallback for Firefox/Safari (download/upload)
// ============================================================================

/**
 * Fallback: Check database file via standard file input.
 */
async function pickAndCheckDatabaseFileFallback(): Promise<FileCheckResult> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".db,.sqlite,.sqlite3";

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }

      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        const encrypted = isEncryptedDatabase(bytes);

        resolve({
          isEncrypted: encrypted,
          file,
          handle: null,
          fileName: file.name,
        });
      } catch (err) {
        reject(err);
      }
    };

    input.oncancel = () => {
      reject(new Error("File picker was cancelled"));
    };

    input.click();
  });
}

/**
 * Fallback: Open database via standard file input.
 */
async function pickDatabaseFileFallback(): Promise<OpenDatabaseResult> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".db,.sqlite,.sqlite3";

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }

      try {
        const db = await openDatabaseFromFile(file);
        resolve({
          db,
          handle: null, // No persistent handle in fallback mode
          fileName: file.name,
          isEncrypted: false,
        });
      } catch (err) {
        reject(err);
      }
    };

    input.oncancel = () => {
      reject(new Error("File picker was cancelled"));
    };

    input.click();
  });
}

/**
 * Fallback: Save database via download link.
 * Optionally encrypts with password.
 */
export async function saveDatabaseFileFallback(
  db: Database,
  fileName: string,
  password?: string | null
): Promise<void> {
  let data: Uint8Array = exportDatabaseBytes(db);

  // Encrypt if password provided
  if (password) {
    data = await encryptDatabase(data, password);
  }

  const blob = new Blob([data.buffer.slice(0) as ArrayBuffer], { type: "application/x-sqlite3" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();

  // Clean up after download initiates
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/**
 * Export database bytes for manual handling.
 */
export function getDatabaseBytes(db: Database): Uint8Array {
  return exportDatabaseBytes(db);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get suggested filename with timestamp.
 */
export function getSuggestedFileName(): string {
  const date = new Date().toISOString().slice(0, 10);
  return `quickgrade-${date}.db`;
}

/**
 * Check if we have a valid file handle with write permission.
 */
export async function hasWritePermission(
  handle: FileSystemFileHandleExt | null
): Promise<boolean> {
  if (!handle || !handle.queryPermission) {
    return false;
  }

  try {
    const permission = await handle.queryPermission({ mode: "readwrite" });
    return permission === "granted";
  } catch {
    return false;
  }
}
